<template>
    <div class="col-sm-12 nopads">
        <h3>{{ priceList.id > 0 ? $t('pricelist.edit') : $t('pricelist.add')}} </h3>
        <div class="row form-data nopads">
                <div class="col-sm-6 nopads">
                    <span class="span-title">
                      {{ $t('pricelist.name') }}
                    </span>
                    <input
                        id="name"
                        v-model="priceListUpdate.name"
                        type="text"
                        class="form-control form-control-sm"
                    />
                </div>
                <div v-if="priceListUpdate.id && priceListUpdate.id > 0" class="col-sm-6 nopads">
                    <span class="span-title">
                        {{ $t('pricelist.active') }}
                    </span>
                    <div class="form-check">
                        <input
                            id="active"
                            v-model="priceListUpdate.active"
                            type="checkbox"
                            class="form-check-input"
                            name="checkbox"
                        />
                    </div>
                </div>
                <div class="row nopads pt-3">
                    <div class="col-3 span-title nopads pr-2">{{ $t('common.area')}}</div>
                    <div class="col-3 span-title nopads pr-2">{{ $t('orders.task_type')}}</div>
                    <div class="col-3 span-title nopads pr-2">{{ $t('orders.unit_price', {currency: currency})}}</div>
                </div>

                <!-- Area prices -->
                <area-price-selector
                    v-for="(item, index) in priceListUpdate.prices"
                    v-bind:key="index"
                    :index="index"
                    :priceItem="item"
                    :errorRow="hasErrorCombination(item)"
                    :areas="areas"
                    :taskTypes="taskTypes"
                    @priceUpdated="updatePrice"
                    @updateEvent="resetErrors"
                />
                <div class="col-sm-12 button-container">
                    <button
                        class="btn btn-success btn-sm"
                        @click.stop="addPriceItem"
                    >
                        <i class="fa fa-plus"/>
                    </button>
                </div>

                <!-- Buttons -->
                <hr/>
                <div class="col-sm-12 button-container">
                    <button
                        class="btn btn-secondary form-button"
                        @click.stop="$emit('close')"
                    >
                        {{ $t('common.cancel') }}
                    </button>
                    <button
                        class="btn btn-success form-button"
                        :disabled="loading > 0 || !priceListUpdate.name || priceListUpdate.name.length < 1 || !validPrices"
                        @click.stop="submitPriceList">
                          {{ $t('common.save') }}
                    </button>
                </div>
            </div>
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '../../mixins/RestApiMixin'
import AreaPriceSelector from "@/components/pricingmodels/pricelists/AreaPriceSelector";
import {inject} from "vue";

export default {
    name: 'AreaPriceListEditor',
    components: {AreaPriceSelector},
    mixins: [restApi],
    props: {
        priceList: {
            type: Object,
            default: null
        },
        hideTitle: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: 0,
            priceListUpdate: this.priceList,
            areas: [],
            taskTypes: [],
            errorCombinations: []
        }
    },
    computed: {
        validPrices() {
            let item = this.priceListUpdate.prices.find(item => !item.area || !item.area.id || !item.task_type ||
                !item.task_type.id || !item.price)
            return item === null || item === undefined
        },
        currency() {
            return import.meta.env.VITE_APP_CURRENCY || '€'
        }
    },

    mounted() {
        this.fetchAreas()
        this.fetchTaskTypes()
    },

    methods: {
        setDefaultValues: function (priceList) {
            if (priceList.name === '') {
                this.showAlert(this.$t('pricelist.invalid_name'))
                return undefined
            }
            if (priceList.prices.length < 1) {
                this.showAlert(this.$t('pricelist.invalid_prices'))
                return undefined
            }
            if(priceList.prices.length > 1) {
                const seenCombinations = [];
                let hasDuplicatePrices = false;

                priceList.prices.forEach((price) => {
                    const combination = [price.area.id, price.task_type.id];

                    // Check if the combination has been seen before
                    if (seenCombinations.some((seenCombination) => this.compareArrays(seenCombination, combination))) {
                        hasDuplicatePrices = true;
                        this.errorCombinations = [combination]
                        return; // Exit the forEach loop early if a duplicate is found
                    }

                    // Add the combination to the array of seen combinations
                    seenCombinations.push(combination);
                });

                if (hasDuplicatePrices) {
                    this.showAlert(this.$t('pricelist.invalid_duplicates'))
                    return undefined
                }
            }
            if (!priceList.created_at) {
                let now = new Date()
                priceList.created_at = now.toISOString()
            }
            return priceList
        },

        compareArrays(arr1, arr2) {
            // Compare two arrays for equality
            return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
        },

        submitPriceList: function () {
            let json = this.setDefaultValues(this.priceListUpdate)
            if(json !== undefined) {
                this.loading = true
                if (!this.priceListUpdate.id || this.priceListUpdate.id < 1) {
                    this.restAdd(this.areaPriceListUrl, json, this.success, this.fail)
                } else {
                    this.restUpdate(this.areaPriceListUrl, json, this.success, this.fail)
                }
            }
        },

        hasErrorCombination(item) {
            return this.errorCombinations.some(
                (errorItem) =>
                    errorItem[0] === item.area.id && errorItem[1] === item.task_type.id
            );
        },

        resetErrors() {
            this.errorCombinations = []
        },

        success: function (response) {
            this.$emit('closeAndUpdate', response)
            this.loading = false
        },

        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },

        addPriceItem() {
            let presetTaskType = {id: null}
            let presetArea = {id: null}
            if (this.priceListUpdate.prices.length > 0) {
                presetArea = JSON.parse(JSON.stringify(this.priceListUpdate.prices[this.priceListUpdate.prices.length-1].area))
                presetTaskType = JSON.parse(JSON.stringify(this.priceListUpdate.prices[this.priceListUpdate.prices.length-1].task_type))
            }
            this.priceListUpdate.prices.push({area: presetArea, task_type: presetTaskType, price: 0.0})
        },

        fetchAreas: function () {
            this.loading++
            this.restFetchParams(this.areaUrl, {no_geom: 1}, this.handleAreas)
        },

        handleAreas: function (response) {
            this.loading--
            if (response && response.data) {
                this.areas = response.data
            }
        },

        fetchTaskTypes: function () {
            this.loading++
            this.restFetch(this.taskTypeUrl, this.handleTaskTypes, this.onTaskTypeLoaded)
        },

        handleTaskTypes: function (response) {
            this.loading--
            if (response && response.data) {
                this.taskTypes = response.data
            }
        },

        updatePrice(index, price) {
            this.priceListUpdate.prices[index] = price
        }

    }
}
</script>
